import { countryName } from '@/ui/words';
import { DemandScorecard, SupplierComparison, SupplierSamples } from './Demand';
import { SupplyRisks } from './FollowThrough';
import { Manufacturers } from './Discovery';
import { useState, type ChangeEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { SUPPLIER_FIELDS } from '@norum/contracts';
import { failureOf, said } from '@/api/errors';
import { Dialog } from '@/ui/Dialog';
import {
  useApproveBuyer,
  useAskSupplier,
  useSupplierAsked,
  useBuyers,
  useConfirmAnswer,
  useMarkOfferSent,
  useOffer,
  useOffers,
  usePrepareOffer,
  useRecordAnswer,
  useSupplier,
  useSuppliers,
  useThreads,
  useCreateBuyer,
  useCreateSupplier,
  useCreateOffer,
  useFindSupplierContact,
  useMarkRecipientSent,
  useMarketList,
  useSendOfferTo,
  useSupplierContact,
} from '@/api/hooks';
import type { BuyerDraft, OfferDraft, SupplierDraft } from '@/api/hooks';
import type { Relationship, WireBuyer, WireOffer, WireOfferRow, WireSupplier, WireSupplierField, WireSupplierRow } from '@/api/wire';
import { ApproveCounterparty } from '@/directory/flows';
import { useMeOrThrow } from '@/app/MeContext';
import { useDepartmentLabel } from '@/app/departments';
import { PageHeader } from '@/app/frame';
import { AgentBrief, DenseBlock } from '@/ui/brief';
import { Select, Textarea } from '@/ui/fields';
import { Badge, Button, Field, Kicker, Skeleton, TextInput, STACK_ON_PHONE } from '@/ui/primitives';
import { FactList, RecordHeader, Table, when } from '@/ui/records';
import { Refusal } from '@/ui/Refusal';
import { Banner } from '@/ui/status';
import { useToast } from '@/ui/Toast';
import { Explainer } from '@/ui/context';
import { useWho } from '@/ui/who';
import { explainBuyer, explainOffer, explainSupplier, offerRowOf, supplierRowOf } from './explain';

/**
 * Market validation (S:18-S:20; ADR-0135): who buys, what the supplier has
 * put in writing, and the offer that rests on it. An answer is recorded as
 * it was given, and counts only once it is confirmed in writing — a
 * document of theirs, or a conversation on file here — and a price-list
 * answer stops counting the day after the list's date. An offer cannot be
 * prepared while any line rests on an answer that is not confirmed (Q18),
 * and the platform sends none of it: the person sends it from their own
 * address.
 */

const decidesIn = (me: ReturnType<typeof useMeOrThrow>) => me.isSuperadmin || me.leads.includes(me.department);
export const CONFIDENCE_CHIP = (c: WireBuyer['confidence']) => ({ label: c.toUpperCase(), tone: c === 'high' ? ('accent' as const) : c === 'medium' ? ('warning' as const) : ('neutral' as const) });
const dayOf = (iso: string | null) => (iso ? new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }) : null);

/** Buyers (S:18): one purchasing contact per company, with how to reach them and how sure we are. */
export function Buyers() {
  const me = useMeOrThrow();
  const label = useDepartmentLabel();
  const navigate = useNavigate();
  const q = useBuyers(me.department);
  const approve = useApproveBuyer();
  const toast = useToast();
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [approvingCompany, setApprovingCompany] = useState<WireBuyer | null>(null);
  const who = useWho();
  if (q.isPending) return <Skeleton rows={8} />;
  if (q.isError) return <div className="p-6"><Refusal failure={failureOf(q.error)} /></div>;
  const reach = (b: WireBuyer) => [b.linkedin && 'LinkedIn', b.phone && 'Phone', b.email && 'Email', b.switchboard && 'Switchboard'].filter(Boolean).join(' · ') || '—';
  const relationship = (b: WireBuyer) => (b.company?.relationship ?? 'unapproved');
  const companyApproved = (b: WireBuyer) => relationship(b) !== 'unapproved';
  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-6 p-6 phone:p-4">
      <PageHeader kicker={label(me.department)} title="Buyers" />
      <div className={`flex flex-wrap justify-end gap-2 ${STACK_ON_PHONE}`}>
        <Button icon="add" size="row" onClick={() => setAdding(true)}>Add a buyer…</Button>
      </div>
      {adding && <AddBuyer onClose={() => setAdding(false)} />}
      <Table<WireBuyer>
        title="buyers"
        testId="buyers"
        blurb="The customer database the validation asks for: one purchasing contact per company, with how to reach them. Wholesalers first — they also tell us who supplies the chains today."
        rows={q.data.buyers}
        onOpen={(b) => setOpenId(b.id === openId ? null : b.id)}
        openId={openId}
        explain={(b) =>
          explainBuyer(b, who, {
            department: me.department,
            mayDecide: decidesIn(me),
            humanId: me.humanId,
            approveCompany: () => setApprovingCompany(b),
            approveBuyer: () => approve.mutate(b.id, { onSuccess: () => toast({ text: 'Approved. They may be written to now.' }), onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) }),
            busy: approve.isPending,
          })
        }
        expanded={(b) => (
          <div className="flex flex-col gap-3 py-2">
            <FactList
              facts={[
                ['Buyer', b.erased ? 'Erased: their details were destroyed' : b.persona ? `${b.roleTitle} (a role, no name on record)` : `${b.name ?? '—'} · ${b.roleTitle}`],
                ['Email', b.email],
                ['Phone', b.phone],
                ['LinkedIn', b.linkedin],
                ['Switchboard', b.switchboard],
                ['Supplied by today', b.suppliedBy],
                ['Where it came from', b.source],
                ['Website', b.website],
              ]}
            />
            <div className="flex flex-wrap gap-2">
              {/* A buyer is approved only at an approved company: the company first, under a token (§17, SG-02). */}
              {!b.approved && decidesIn(me) && !companyApproved(b) && (b.company || b.prospectId) && (
                <Button icon="check" size="row" variant="primary" onClick={() => setApprovingCompany(b)}>Approve the company…</Button>
              )}
              {!b.approved && decidesIn(me) && !companyApproved(b) && !b.company && !b.prospectId && (
                <span className="text-label text-meta">Approve the company on Companies first, under a token; then this buyer.</span>
              )}
              {!b.approved && decidesIn(me) && companyApproved(b) && (
                <Button icon="check" size="row" variant="primary" disabled={approve.isPending} onClick={() => approve.mutate(b.id, { onSuccess: () => toast({ text: 'Approved. They may be written to now.' }), onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) })}>Approve them</Button>
              )}
              {b.prospectId && <Button size="row" variant="quiet" onClick={() => void navigate(`/prospects/${b.prospectId}`)}>The prospect</Button>}
              <Button size="row" variant="quiet" onClick={() => void navigate(`/buyers/${b.id}`)}>Its record</Button>
            </div>
          </div>
        )}
        emptyTitle="No buyer yet"
        emptyBody="The real data loads the companies the market reports name, as roles; a person adds the buyer found."
        columns={[
          { label: 'Company', get: (b) => b.companyName, primary: true, chip: (b) => ({ label: relationship(b).toUpperCase(), tone: companyApproved(b) ? 'accent' : 'neutral' }) },
          { label: 'Type', get: (b) => b.companyType ?? '—' },
          { label: 'Buyer', get: (b) => (b.erased ? 'Erased' : b.persona ? b.roleTitle : `${b.name ?? '—'} · ${b.roleTitle}`) },
          { label: 'Reach', get: reach },
          { label: 'Confidence', get: (b) => b.confidence, chip: (b) => CONFIDENCE_CHIP(b.confidence) },
        ]}
        facets={[
          { label: 'Type', get: (b) => b.companyType ?? '—' },
          { label: 'Relationship', get: (b) => relationship(b) },
        ]}
      />
      {approvingCompany && (
        <ApproveCounterparty
          kind="company"
          id={approvingCompany.company?.id ?? ''}
          name={approvingCompany.company?.name ?? approvingCompany.companyName}
          current={(approvingCompany.company?.relationship as Relationship | undefined) ?? 'unapproved'}
          prospectId={approvingCompany.prospectId ?? undefined}
          preset="client"
          onClose={() => setApprovingCompany(null)}
        />
      )}
    </div>
  );
}

export function Suppliers() {
  const me = useMeOrThrow();
  const label = useDepartmentLabel();
  const navigate = useNavigate();
  const q = useSuppliers(me.department);
  const [adding, setAdding] = useState(false);
  const who = useWho();
  if (q.isPending) return <Skeleton rows={6} />;
  if (q.isError) return <div className="p-6"><Refusal failure={failureOf(q.error)} /></div>;
  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-6 p-6 phone:p-4">
      <PageHeader kicker={label(me.department)} title="Suppliers" />
      {decidesIn(me) && (
        <div className={`flex flex-wrap justify-end gap-2 ${STACK_ON_PHONE}`}>
          <Button icon="add" size="row" onClick={() => setAdding(true)}>Add a supplier…</Button>
        </div>
      )}
      {adding && <AddSupplier onClose={() => setAdding(false)} />}
      <Manufacturers />
      <Table<WireSupplierRow>
        title="suppliers"
        testId="suppliers"
        blurb={`Who makes what we sell, and what they have put in writing of the ${SUPPLIER_FIELDS.length} questions a first order needs answered.`}
        rows={q.data.suppliers}
        onOpen={(x) => void navigate(`/suppliers/${x.id}`)}
        explain={(x) => explainSupplier(x, who, { department: me.department, mayDecide: decidesIn(me), humanId: me.humanId })}
        emptyTitle="No supplier yet"
        emptyBody="The real data loads the current manufacturer."
        columns={[
          { label: 'Supplier', get: (x) => x.name, primary: true },
          { label: 'Country', get: (x) => x.country },
          { label: 'Role', get: (x) => (x.role === 'current' ? 'Current manufacturer' : 'Alternative') },
          { label: 'Answered', get: (x) => `${x.answered} of ${SUPPLIER_FIELDS.length}` },
          { label: 'In writing', get: (x) => `${x.confirmed} of ${SUPPLIER_FIELDS.length}` },
          { label: 'Price list', get: (x) => (x.priceListExpired ? `Expired ${dayOf(x.priceListValidUntil)}` : x.priceListValidUntil ? `Valid to ${dayOf(x.priceListValidUntil)}` : '—') },
          { label: 'Next step', get: (x) => NEXT_STEP[x.nextStep ?? 'ask'].label, chip: (x) => NEXT_STEP[x.nextStep ?? 'ask'] },
        ]}
        facets={[
          { label: 'Role', get: (x) => (x.role === 'current' ? 'Current manufacturer' : 'Alternative') },
          { label: 'Next step', get: (x) => NEXT_STEP[x.nextStep ?? 'ask'].label },
        ]}
      />
      <SupplierComparison />
      <SupplyRisks />
    </div>
  );
}

/** What a person does next with a supplier, derived on the server from the fields and never stored (S:19). */
const NEXT_STEP: Record<NonNullable<WireSupplier['nextStep']>, { label: string; tone: 'accent' | 'warning' | 'danger' | 'neutral' }> = {
  add_contact: { label: 'Add a contact', tone: 'warning' },
  ask: { label: 'Ask for what’s missing', tone: 'warning' },
  renew_price_list: { label: 'Price list expired', tone: 'danger' },
  confirm: { label: 'Confirm in writing', tone: 'warning' },
  ready: { label: 'Ready for an order', tone: 'accent' },
};

function fieldState(f: WireSupplierField): string {
  if (!f.answer) return 'Not answered';
  if (f.expired) return `${f.answer} — the price list it rests on has expired`;
  if (!f.confirmed) return `${f.answer} — said, not yet in writing`;
  return f.answer;
}

/** A supplier (S:19): the fourteen questions, what is answered and in writing, and asking for the rest. */
export function SupplierPage({ recordId }: { recordId?: string } = {}) {
  const params = useParams();
  const id = recordId ?? params.id ?? '';
  const q = useSupplier(id);
  const ask = useAskSupplier();
  const asked = useSupplierAsked();
  // The draft this visit made, so "I sent it" closes its task too.
  const [draftTask, setDraftTask] = useState<string | null>(null);
  const find = useFindSupplierContact();
  const toast = useToast();
  const [recording, setRecording] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const me = useMeOrThrow();
  const who = useWho();
  if (q.isPending) return <Skeleton rows={12} />;
  if (q.isError) return <div className="p-6"><Refusal failure={failureOf(q.error)} /></div>;
  const x = q.data.supplier;
  const open = SUPPLIER_FIELDS.length - x.fields.filter((f) => f.confirmed && !f.expired).length;
  const unanswered = x.fields.filter((f) => !f.answer).length;
  const next = NEXT_STEP[x.nextStep ?? 'ask'];
  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-8 p-6 phone:p-4" data-testid="supplier-page">
      <RecordHeader
        kicker="Supplier"
        title={x.name}
        subtitle={`${x.country} · ${x.role === 'current' ? 'current manufacturer' : 'an alternative'}`}
        meta={[x.contact ?? 'no contact on record', x.asOf ? `answers as of ${dayOf(x.asOf)}` : null, x.askedAt ? `asked ${when(x.askedAt)}${x.askedBy ? ` by ${x.askedBy}` : ''}` : null].filter(Boolean).join(' · ') || null}
        chips={[next]}
        back={{ to: '/suppliers', label: 'Suppliers' }}
        actions={
          <>
            {!x.contact && (
              <Button icon="ask"
                disabled={find.isPending}
                onClick={() =>
                  find.mutate(x.id, {
                    onSuccess: (r) =>
                      toast({
                        text:
                          r.routedTo === 'agent'
                            ? `${r.agent} has it as a task: their site and the register, and writes to nobody.`
                            : r.routing?.outcome === 'refused'
                              ? `Outbound cannot take it: ${r.routing.reason ?? 'no grant'}.`
                              : 'Asked of Outbound by request; nothing is sent.',
                      }),
                    onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
                  })
                }
              >Ask Otis to find one</Button>
            )}
            <Button onClick={() => setEditingContact(!editingContact)}>{x.contact ? 'Change the contact' : 'Add the contact'}</Button>
            <Button icon="ask"
              variant="primary"
              disabled={ask.isPending || open === 0}
              onClick={() =>
                ask.mutate(x.id, {
                  onSuccess: (r) => {
                    setDraftTask(r.taskId);
                    toast({ text: `A draft asking ${r.asked.length} questions is on your day: change it as you like, send it from your own address, then press “I sent it”.`, link: { label: 'Edit the draft', to: `/tasks/${r.taskId}` } });
                  },
                  onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
                })
              }
            >Ask them for what’s missing</Button>
            {open > 0 && !x.askedAt && (
              <Button
                disabled={asked.isPending}
                onClick={() =>
                  asked.mutate(
                    { supplierId: x.id, ...(draftTask ? { taskId: draftTask } : {}) },
                    { onSuccess: () => toast({ text: `${x.name} is marked asked, today.` }), onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) },
                  )
                }
              >
                I sent it
              </Button>
            )}
            <Button onClick={() => setRecording(!recording)}>Record their answer</Button>
          </>
        }
      />
      <Explainer context={explainSupplier(supplierRowOf(x), who, { department: me.department, mayDecide: decidesIn(me), onPage: true, humanId: me.humanId })} startOpen />
      {editingContact && <ContactForm supplierId={x.id} current={x.contact} onDone={() => setEditingContact(false)} />}
      {recording && <RecordAnswer supplierId={x.id} documentId={x.documentId} onDone={() => setRecording(false)} />}
      {open === 0 && <Banner tone="accent" title="Every answer is in, and in writing" />}
      <DenseBlock
        label={open > 0 ? `${unanswered} of ${SUPPLIER_FIELDS.length} unanswered${x.priceListExpired ? ' · price list expired' : ''} — ${open} to have in writing` : `${SUPPLIER_FIELDS.length} of ${SUPPLIER_FIELDS.length} in writing`}
        {...(open > 0 ? { tone: 'warning' as const } : {})}
        rows={x.fields.map((f): [string, ReactNode] => [f.label, <FieldLine key={f.key} f={f} canConfirm={Boolean(f.answerId) && !f.confirmed} documentId={x.documentId} />])}
      >
        {open > 0
          ? 'Every one of these is a line in the commercial offer that cannot go to a customer until it is answered, and answered in writing.'
          : 'Each answer stays visible with where it is written, so an offer’s line can be read back to it.'}
      </DenseBlock>
      <FactList
        facts={[
          ['Next step', next.label],
          ['Price list', x.priceListValidUntil ? `${x.priceListExpired ? 'Expired' : 'Valid until'} ${dayOf(x.priceListValidUntil)}` : null],
          ['Price list document', x.priceListDocument ?? (x.documentId ? 'On file' : 'None on file — a confirmation names a conversation instead')],
          ['Last asked', x.askedAt ? `${when(x.askedAt)}${x.askedBy ? `, by ${x.askedBy}, from their own address` : ''}` : 'Never'],
          ['Incoterms', x.incoterms],
          ['Order minimum', x.orderMinimum],
        ]}
      />
      <AgentBrief lines={[['supplier', x.id], ['country', x.country], ['role', x.role], ['next_step', x.nextStep], ['missing', x.missing.join(',') || null], ['unconfirmed', x.unconfirmed.join(',') || null], ['price_list_expired', x.priceListExpired], ['has_contact', Boolean(x.contact)]]} />
      <SupplierSamples supplierId={id} />
    </div>
  );
}

/** The supplier's contact, as a person gives it: a role address or a name, sealed under the supplier's key and never listed. */
function ContactForm({ supplierId, current, onDone }: { supplierId: string; current: string | null; onDone: () => void }) {
  const save = useSupplierContact();
  const toast = useToast();
  const [contact, setContact] = useState(current ?? '');
  return (
    <form
      className="flex flex-wrap items-end gap-2 border border-strong p-4"
      aria-label="The contact"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate({ supplierId, contact: contact.trim() }, { onSuccess: () => { toast({ text: 'Recorded. The questions go to them from your own address.' }); onDone(); }, onError: (err) => toast({ text: said(failureOf(err)), tone: 'danger' }) });
      }}
    >
      <div className="min-w-[280px] flex-1">
        <Field label="Who to write to" hint="A role address or a name, as they gave it: ventas@…, or Natividad Vaquero.">
          <TextInput aria-label="Who to write to" value={contact} onChange={(e) => setContact(e.target.value)} maxLength={300} />
        </Field>
      </div>
      <Button type="submit" variant="primary" disabled={save.isPending || contact.trim().length === 0}>Record the contact</Button>
      <Button icon="close" variant="tertiary" onClick={onDone}>Cancel</Button>
    </form>
  );
}

function FieldLine({ f, canConfirm, documentId }: { f: WireSupplierField; canConfirm: boolean; documentId: string | null }) {
  const me = useMeOrThrow();
  const confirm = useConfirmAnswer();
  const threads = useThreads(me.department);
  const toast = useToast();
  const [asking, setAsking] = useState(false);
  const [thread, setThread] = useState('');
  const done = { onSuccess: () => toast({ text: 'Confirmed in writing.' }), onError: (e: unknown) => toast({ text: said(failureOf(e)), tone: 'danger' }) };
  return (
    <span className="flex flex-col gap-1.5" data-testid={`field-${f.key}`}>
      <span className={f.confirmed && !f.expired ? '' : 'text-secondary'}>{fieldState(f)}</span>
      {f.note && <span className="text-label text-secondary" data-testid={`note-${f.key}`}>Note: {f.note}</span>}
      {f.source && <span className="text-label text-meta">From {f.source}{f.recordedAt ? ` · ${when(f.recordedAt)}` : ''}{f.confirmedIn ? ` · in writing in ${'document' in f.confirmedIn ? 'their document' : 'a conversation on file'}` : ''}</span>}
      {canConfirm && !asking && (
        <span>
          <Button size="row" variant="quiet" onClick={() => setAsking(true)}>Confirm it in writing</Button>
        </span>
      )}
      {canConfirm && asking && (
        <span className="flex flex-wrap items-end gap-2">
          {documentId && (
            <Button size="row" disabled={confirm.isPending} onClick={() => confirm.mutate({ answerId: f.answerId!, documentId }, done)}>
              It is in their price list
            </Button>
          )}
          <Field label="Or the conversation it is written in">
            <Select
              aria-label="The conversation it is written in"
              value={thread}
              onChange={(e) => setThread(e.target.value)}
              options={[{ value: '', label: 'Choose a conversation' }, ...(threads.data?.threads ?? []).map((t) => ({ value: t.id, label: t.subject ?? 'A conversation' }))]}
            />
          </Field>
          <Button icon="check" size="row" disabled={confirm.isPending || !thread} onClick={() => confirm.mutate({ answerId: f.answerId!, threadId: thread }, done)}>Confirm</Button>
        </span>
      )}
    </span>
  );
}

function RecordAnswer({ supplierId, documentId, onDone }: { supplierId: string; documentId: string | null; onDone: () => void }) {
  const me = useMeOrThrow();
  const record = useRecordAnswer();
  const threads = useThreads(me.department);
  const toast = useToast();
  const [field, setField] = useState<string>(SUPPLIER_FIELDS[0].key);
  const [answer, setAnswer] = useState('');
  const [source, setSource] = useState('');
  const [note, setNote] = useState('');
  const [inWriting, setInWriting] = useState<'no' | 'document' | 'thread'>('no');
  const [thread, setThread] = useState('');
  const question = SUPPLIER_FIELDS.find((f) => f.key === field)?.question;
  const confirm = inWriting === 'document' && documentId ? { documentId } : inWriting === 'thread' && thread ? { threadId: thread } : null;
  return (
    <form
      className="flex flex-col gap-3 border border-strong p-4"
      aria-label="Record their answer"
      onSubmit={(e) => {
        e.preventDefault();
        record.mutate(
          { supplierId, field, answer: answer.trim(), source: source.trim(), ...(note.trim() ? { note: note.trim() } : {}), ...(confirm ? { confirm } : {}) },
          {
            onSuccess: (r) => {
              onDone();
              toast({ text: r.confirmed ? 'Recorded, and confirmed in writing.' : 'Recorded as they gave it. It counts once it is confirmed in writing.' });
            },
            onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
          },
        );
      }}
    >
      <Field label="The question" hint={question}>
        <Select aria-label="The question" value={field} onChange={(e) => setField(e.target.value)} options={SUPPLIER_FIELDS.map((f) => ({ value: f.key, label: f.label }))} />
      </Field>
      <Field label="Their answer, as they gave it">
        <Textarea aria-label="Their answer, as they gave it" value={answer} onChange={(e) => setAnswer(e.target.value)} className="min-h-[72px] w-full" />
      </Field>
      <Field label="Where they gave it" hint="A call on 24 September, their email of 20 September, the price list of 17 August.">
        <TextInput aria-label="Where they gave it" value={source} onChange={(e) => setSource(e.target.value)} />
      </Field>
      <Field label="A note" hint="Optional: what to bear in mind about this answer.">
        <TextInput aria-label="A note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
      </Field>
      <Field label="Is it in writing?" hint="Said is not confirmed: only their document on file or a conversation on file counts (ADR-0135 §2).">
        <Select
          aria-label="Is it in writing?"
          value={inWriting}
          onChange={(e) => setInWriting(e.target.value as 'no')}
          options={[
            { value: 'no', label: 'No — said, to confirm later' },
            ...(documentId ? [{ value: 'document', label: 'Yes, in their price list on file' }] : []),
            { value: 'thread', label: 'Yes, in a conversation on file' },
          ]}
        />
      </Field>
      {inWriting === 'thread' && (
        <Field label="The conversation it is written in">
          <Select
            aria-label="The conversation it is written in"
            value={thread}
            onChange={(e) => setThread(e.target.value)}
            options={[{ value: '', label: 'Choose a conversation' }, ...(threads.data?.threads ?? []).map((t) => ({ value: t.id, label: t.subject ?? 'A conversation' }))]}
          />
        </Field>
      )}
      <div>
        <Button type="submit" variant="primary" disabled={record.isPending || !answer.trim() || !source.trim() || (inWriting === 'thread' && !thread)}>
          {confirm ? 'Record it, in writing' : 'Record it'}
        </Button>
      </div>
    </form>
  );
}

const OFFER_CHIP = (s: WireOfferRow['state']) =>
  s === 'draft' ? { label: 'DRAFT', tone: 'warning' as const } : s === 'prepared' ? { label: 'READY TO SEND', tone: 'accent' as const } : s === 'sent' ? { label: 'SENT', tone: 'accent' as const } : { label: 'WITHDRAWN', tone: 'neutral' as const };

export function Offers() {
  const me = useMeOrThrow();
  const label = useDepartmentLabel();
  const navigate = useNavigate();
  const q = useOffers(me.department);
  const [drafting, setDrafting] = useState(false);
  const who = useWho();
  if (q.isPending) return <Skeleton rows={6} />;
  if (q.isError) return <div className="p-6"><Refusal failure={failureOf(q.error)} /></div>;
  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-6 p-6 phone:p-4">
      <PageHeader kicker={label(me.department)} title="Commercial offers" />
      {decidesIn(me) && (
        <div className={`flex flex-wrap justify-end gap-2 ${STACK_ON_PHONE}`}>
          <Button size="row" onClick={() => setDrafting(true)}>Draft an offer…</Button>
        </div>
      )}
      {drafting && <DraftOffer onClose={() => setDrafting(false)} />}
      <Table<WireOfferRow>
        title="offers"
        testId="offers"
        blurb="Each line rests on something the supplier answered. An offer cannot be prepared while a line rests on an answer not in writing."
        rows={q.data.offers}
        onOpen={(o) => void navigate(`/offers/${o.id}`)}
        explain={(o) => explainOffer(o, who, { department: me.department, mayDecide: decidesIn(me), humanId: me.humanId })}
        emptyTitle="No offer yet"
        emptyBody="The lead drafts one for a market, from the supplier’s record."
        columns={[
          { label: 'Offer', get: (o) => o.title, primary: true },
          { label: 'Country', get: (o) => countryName(o.marketCode) },
          { label: 'Supplier', get: (o) => o.supplierName },
          { label: 'Lines', get: (o) => o.lineCount, num: true },
          { label: 'Unconfirmed', get: (o) => o.blocking.length, num: true },
          { label: 'Terms', get: (o) => [o.incoterms, o.validUntil ? `valid to ${dayOf(o.validUntil)}` : null].filter(Boolean).join(' · ') || '—' },
          { label: 'State', get: (o) => OFFER_CHIP(o.state).label, chip: (o) => OFFER_CHIP(o.state) },
        ]}
        facets={[{ label: 'State', get: (o) => OFFER_CHIP(o.state).label }, { label: 'Country', get: (o) => countryName(o.marketCode) }]}
      />
      <DemandScorecard />
    </div>
  );
}

/** An offer (S:20): what blocks it, line by line; then prepared as the preparer's task, sent from their address. */
export function OfferPage({recordId}:{recordId?:string}={}) {
  const params=useParams();
  const id=recordId??params.id??'';
  const me = useMeOrThrow();
  const navigate = useNavigate();
  const q = useOffer(id);
  const prepare = usePrepareOffer();
  const sent = useMarkOfferSent();
  const toast = useToast();
  const who = useWho();
  if (q.isPending) return <Skeleton rows={12} />;
  if (q.isError) return <div className="p-6"><Refusal failure={failureOf(q.error)} /></div>;
  const o = q.data.offer;
  const decides = decidesIn(me);
  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-8 p-6 phone:p-4" data-testid="offer-page">
      <RecordHeader
        kicker="Commercial offer"
        title={o.title}
        subtitle={[o.state === 'draft' ? 'Draft' : o.state === 'prepared' ? 'Ready to send' : o.state === 'sent' ? 'Sent' : 'Withdrawn', o.incoterms].filter(Boolean).join(' · ')}
        meta={[`For ${o.marketCode}`, `from ${o.supplierName}`, o.validUntil ? `valid until ${dayOf(o.validUntil)}` : null, o.sentAt ? `sent ${when(o.sentAt)}` : null].filter(Boolean).join(' · ')}
        chips={[OFFER_CHIP(o.state)]}
        back={{ to: '/offers', label: 'Commercial offers' }}
        actions={
          decides && o.state === 'draft' ? (
            <Button icon="forward"
              variant="primary"
              disabled={prepare.isPending}
              onClick={() =>
                prepare.mutate(o.id, {
                  // A refusal — a line still unconfirmed — is shown on the page, beside the lines it names.
                  onSuccess: () => toast({ text: 'Prepared. Its text is on your day, to send from your own address.', link: { label: 'Today', to: '/today' } }),
                })
              }
            >Send the offer</Button>
          ) : decides && o.state === 'prepared' ? (
            <>
              {o.preparedTaskId && <Button onClick={() => void navigate('/today')}>Its text is on Today</Button>}
              <Button variant="primary" disabled={sent.isPending} onClick={() => sent.mutate(o.id, { onSuccess: () => toast({ text: 'Recorded as sent.' }), onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) })}>
                I have sent it
              </Button>
            </>
          ) : undefined
        }
      />
      <Explainer context={explainOffer(offerRowOf(o), who, { department: me.department, mayDecide: decides, onPage: true })} startOpen />
      {prepare.isError && <Refusal failure={failureOf(prepare.error)} compact />}
      {o.blocking.length > 0 && (
        <DenseBlock
          label={`Subject to confirmation — ${o.blocking.length} line${o.blocking.length === 1 ? ' is' : 's are'} unconfirmed`}
          rows={o.blocking.map((b): [string, ReactNode] => [b.line, `depends on ${SUPPLIER_FIELDS.find((f) => f.key === b.field)?.label.toLowerCase() ?? b.field} — ${b.why}`])}
        >
          Every figure in the offer is confirmed by the manufacturer before a customer sees it. The lines below rest on answers the manufacturer has not put in writing, so it cannot be prepared yet.
        </DenseBlock>
      )}
      <section className="flex flex-col gap-2.5" aria-label="The offer’s lines">
        <Kicker>The offer, line by line</Kicker>
        <FactList
          facts={o.lines.map((l): [string, ReactNode] => [
            l.label,
            <span key={l.position} className="flex flex-wrap items-center gap-2">
              <span className={l.ready ? '' : 'text-secondary'}>{l.value ?? l.why ?? '—'}</span>
              {!l.ready && <Badge tone="warning">UNCONFIRMED</Badge>}
            </span>,
          ])}
        />
      </section>
      <FactList
        facts={[
          ['Currency', o.currency],
          ['For', o.audience.join(', ') || null],
          ['Languages', o.languages.join(', ')],
          ['Drafted by', o.draftedBy ? (o.draftedBy.kind === 'agent' ? `${o.draftedBy.name} (an agent’s draft; a person decides it)` : o.draftedBy.name) : null],
        ]}
      />
      {(o.state === 'prepared' || o.state === 'sent') && <Recipients offer={o} decides={decides} />}
      <AgentBrief lines={[['offer', o.id], ['market', o.marketCode], ['supplier', o.supplierId], ['state', o.state], ['lines', o.lines.length], ['unconfirmed', o.blocking.length], ['recipients', o.recipients?.length ?? 0], ['drafted_by', o.draftedBy?.kind]]} />
    </div>
  );
}


/**
 * Who the offer goes to (S:20): each an approved buyer in its market, its
 * text put on the person's day addressed to them, and marked sent only by
 * that person. Prepare-and-confirm holds for every recipient (ADR-0135).
 */
function Recipients({ offer, decides }: { offer: WireOffer; decides: boolean }) {
  const me = useMeOrThrow();
  const buyers = useBuyers(me.department);
  const sendTo = useSendOfferTo();
  const sent = useMarkRecipientSent();
  const toast = useToast();
  const [buyerId, setBuyerId] = useState('');
  const recipients = offer.recipients ?? [];
  const candidates = (buyers.data?.buyers ?? []).filter((b) => b.marketCode === offer.marketCode && b.approved && !b.erased && !recipients.some((r) => r.buyerId === b.id));
  return (
    <section className="flex flex-col gap-2.5" aria-label="Sent to">
      <Kicker>Sent to</Kicker>
      {recipients.length === 0 ? (
        <p className="m-0 text-dense text-meta">Not addressed to a buyer yet.</p>
      ) : (
        <FactList
          facts={recipients.map((r): [string, ReactNode] => [
            r.company,
            <span key={r.id} className="flex flex-wrap items-center gap-2">
              <span>{r.roleTitle}</span>
              {r.sentAt ? (
                <span className="text-meta">sent {when(r.sentAt)}{r.sentBy ? ` by ${r.sentBy}` : ''}</span>
              ) : (
                <>
                  <Badge tone="warning">ON YOUR DAY</Badge>
                  {decides && (
                    <Button size="row" disabled={sent.isPending} onClick={() => sent.mutate(r.id, { onSuccess: () => toast({ text: `Recorded as sent to ${r.company}.` }), onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) })}>
                      I have sent it
                    </Button>
                  )}
                </>
              )}
            </span>,
          ])}
        />
      )}
      {decides && (
        <div className="flex flex-wrap items-end gap-2">
          <Field label={recipients.length ? 'Send to another buyer' : 'Send to a buyer'} hint="An approved buyer in this market. Its text goes on your day, addressed to them; nothing is sent by the platform.">
            <Select
              aria-label="Send to a buyer"
              value={buyerId}
              onChange={(e) => setBuyerId(e.target.value)}
              options={[{ value: '', label: candidates.length ? 'Choose a buyer' : 'No approved buyer left in this market' }, ...candidates.map((b) => ({ value: b.id, label: `${b.companyName} · ${b.roleTitle}` }))]}
            />
          </Field>
          <Button
            variant="primary"
            disabled={sendTo.isPending || !buyerId}
            onClick={() =>
              sendTo.mutate({ offerId: offer.id, buyerId }, { onSuccess: () => { setBuyerId(''); toast({ text: 'Its text is on your day, addressed to them. Say here when you have sent it.', link: { label: 'Today', to: '/today' } }); }, onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) })
            }
          >
            Put it on my day
          </Button>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Adding by hand: a buyer, a supplier, an offer (PR-15, B12, Q18). Each is
// the route's own body and nothing more; what a person may not do, the
// route refuses and the refusal is shown as it was said.
// ---------------------------------------------------------------------------

const CODE = /^[A-Z]{2}$/;

/** A buyer found: a role at a company, how to reach them, and where it came from. Unapproved until the lead says so. */
export function AddBuyer({ onClose }: { onClose: () => void }) {
  const create = useCreateBuyer();
  const toast = useToast();
  const [d, setD] = useState<BuyerDraft>({ marketCode: '', companyName: '', roleTitle: '', source: '', confidence: 'medium' });
  const set = (k: keyof BuyerDraft) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setD((x) => ({ ...x, [k]: e.target.value }));
  const optional = (v: string | undefined) => (v && v.trim() !== '' ? v.trim() : undefined);
  const ready = CODE.test(d.marketCode.trim().toUpperCase()) && d.companyName.trim() !== '' && d.roleTitle.trim() !== '' && d.source.trim() !== '';
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Buyers"
      title="Add a buyer"
      note="A person, not a name from a list: the lead approves them before anyone writes to them."
      primary={{
        label: 'Add the buyer',
        disabled: !ready || create.isPending,
        onClick: () =>
          create.mutate(
            {
              marketCode: d.marketCode.trim().toUpperCase(),
              companyName: d.companyName.trim(),
              roleTitle: d.roleTitle.trim(),
              source: d.source.trim(),
              confidence: d.confidence,
              ...(optional(d.companyType) ? { companyType: d.companyType!.trim() } : {}),
              ...(optional(d.website) ? { website: d.website!.trim() } : {}),
              ...(optional(d.name) ? { name: d.name!.trim() } : {}),
              ...(optional(d.email) ? { email: d.email!.trim() } : {}),
              ...(optional(d.phone) ? { phone: d.phone!.trim() } : {}),
              ...(optional(d.linkedin) ? { linkedin: d.linkedin!.trim() } : {}),
              ...(optional(d.switchboard) ? { switchboard: d.switchboard!.trim() } : {}),
              ...(optional(d.suppliedBy) ? { suppliedBy: d.suppliedBy!.trim() } : {}),
            },
            { onSuccess: () => { toast({ text: 'Added. The lead approves them before anyone writes.' }); onClose(); }, onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) },
          ),
      }}
      secondary={{ label: 'Cancel', onClick: onClose }}
    >
      <Field label="Market" hint="Two letters, e.g. SE."><TextInput aria-label="Market" value={d.marketCode} onChange={set('marketCode')} maxLength={2} /></Field>
      <Field label="Company"><TextInput aria-label="Company" value={d.companyName} onChange={set('companyName')} /></Field>
      <Field label="Type" hint="Wholesaler, restaurant group, retailer…"><TextInput aria-label="Type" value={d.companyType ?? ''} onChange={set('companyType')} /></Field>
      <Field label="Role" hint="Their title: purchasing manager, head chef."><TextInput aria-label="Role" value={d.roleTitle} onChange={set('roleTitle')} /></Field>
      <Field label="Name" hint="Leave empty to keep them as a role only."><TextInput aria-label="Name" value={d.name ?? ''} onChange={set('name')} /></Field>
      <Field label="Email"><TextInput aria-label="Email" value={d.email ?? ''} onChange={set('email')} /></Field>
      <Field label="Phone"><TextInput aria-label="Phone" value={d.phone ?? ''} onChange={set('phone')} /></Field>
      <Field label="LinkedIn"><TextInput aria-label="LinkedIn" value={d.linkedin ?? ''} onChange={set('linkedin')} /></Field>
      <Field label="Switchboard"><TextInput aria-label="Switchboard" value={d.switchboard ?? ''} onChange={set('switchboard')} /></Field>
      <Field label="Supplied by today"><TextInput aria-label="Supplied by today" value={d.suppliedBy ?? ''} onChange={set('suppliedBy')} /></Field>
      <Field label="Website"><TextInput aria-label="Website" value={d.website ?? ''} onChange={set('website')} /></Field>
      <Field label="Where it came from" hint="The page, the call or the person who said so."><TextInput aria-label="Where it came from" value={d.source} onChange={set('source')} /></Field>
      <Field label="Confidence">
        <Select aria-label="Confidence" value={d.confidence} onChange={set('confidence')} options={[{ value: 'high', label: 'High' }, { value: 'medium', label: 'Medium' }, { value: 'low', label: 'Low' }]} />
      </Field>
    </Dialog>
  );
}

/** A supplier: who makes what we sell, and what their price list rests on. The lead's to add. */
export function AddSupplier({ onClose }: { onClose: () => void }) {
  const create = useCreateSupplier();
  const toast = useToast();
  const [d, setD] = useState<SupplierDraft>({ name: '', country: '', role: 'alternative' });
  const set = (k: keyof SupplierDraft) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setD((x) => ({ ...x, [k]: e.target.value }));
  const ready = d.name.trim() !== '' && CODE.test(d.country.trim().toUpperCase());
  const opt = (k: keyof SupplierDraft) => (d[k] && String(d[k]).trim() !== '' ? { [k]: String(d[k]).trim() } : {});
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Suppliers"
      title="Add a supplier"
      note={`Their answers to the ${SUPPLIER_FIELDS.length} questions are recorded on their page, one by one, and confirmed only in writing.`}
      primary={{
        label: 'Add the supplier',
        disabled: !ready || create.isPending,
        onClick: () =>
          create.mutate(
            { name: d.name.trim(), country: d.country.trim().toUpperCase(), role: d.role, ...opt('contact'), ...opt('asOf'), ...opt('priceListValidUntil'), ...opt('incoterms'), ...opt('orderMinimum') },
            { onSuccess: () => { toast({ text: 'Added. Record what they have answered on their page.' }); onClose(); }, onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) },
          ),
      }}
      secondary={{ label: 'Cancel', onClick: onClose }}
    >
      <Field label="Supplier"><TextInput aria-label="Supplier" value={d.name} onChange={set('name')} /></Field>
      <Field label="Country" hint="Two letters, e.g. ES."><TextInput aria-label="Country" value={d.country} onChange={set('country')} maxLength={2} /></Field>
      <Field label="Role">
        <Select aria-label="Role" value={d.role} onChange={set('role')} options={[{ value: 'current', label: 'Current manufacturer' }, { value: 'alternative', label: 'Alternative' }]} />
      </Field>
      <Field label="Contact" hint="A role address or a name, as they gave it."><TextInput aria-label="Contact" value={d.contact ?? ''} onChange={set('contact')} /></Field>
      <Field label="Price list dated" hint="YYYY-MM-DD"><TextInput aria-label="Price list dated" value={d.asOf ?? ''} onChange={set('asOf')} /></Field>
      <Field label="Price list valid until" hint="YYYY-MM-DD"><TextInput aria-label="Price list valid until" value={d.priceListValidUntil ?? ''} onChange={set('priceListValidUntil')} /></Field>
      <Field label="Incoterms"><TextInput aria-label="Incoterms" value={d.incoterms ?? ''} onChange={set('incoterms')} /></Field>
      <Field label="Order minimum"><TextInput aria-label="Order minimum" value={d.orderMinimum ?? ''} onChange={set('orderMinimum')} /></Field>
    </Dialog>
  );
}

/** An offer drafted from a supplier's record: one line per row, each naming the answer it rests on. */
export function DraftOffer({ onClose }: { onClose: () => void }) {
  const me = useMeOrThrow();
  const suppliers = useSuppliers(me.department);
  const buyers = useBuyers(me.department);
  const markets = useMarketList(me.department);
  const create = useCreateOffer();
  const toast = useToast();
  const [d, setD] = useState<Omit<OfferDraft, 'lines'>>({ marketCode: '', supplierId: '', title: '', currency: 'EUR' });
  const [lines, setLines] = useState('');
  const [forBuyers, setForBuyers] = useState<string[]>([]);
  const [language, setLanguage] = useState('en');
  const code = d.marketCode.trim().toUpperCase();
  const market = markets.data?.markets.find((m) => m.marketCode === code);
  const own = market?.language ?? null;
  const approvedBuyers = (buyers.data?.buyers ?? []).filter((b) => b.marketCode === code && b.approved && !b.erased);
  const set = (k: keyof typeof d) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setD((x) => ({ ...x, [k]: e.target.value }));
  const parsed = offerLines(lines);
  const ready = CODE.test(d.marketCode.trim().toUpperCase()) && d.supplierId !== '' && d.title.trim() !== '' && /^[A-Z]{3}$/.test(d.currency.trim().toUpperCase()) && parsed.length > 0;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Commercial offers"
      title="Draft an offer"
      note="It cannot be prepared while a line rests on an answer not in writing (ADR-0135)."
      primary={{
        label: 'Draft it',
        disabled: !ready || create.isPending,
        onClick: () =>
          create.mutate(
            {
              marketCode: d.marketCode.trim().toUpperCase(),
              supplierId: d.supplierId,
              title: d.title.trim(),
              currency: d.currency.trim().toUpperCase(),
              ...(d.validUntil && d.validUntil.trim() !== '' ? { validUntil: d.validUntil.trim() } : {}),
              ...(d.incoterms && d.incoterms.trim() !== '' ? { incoterms: d.incoterms.trim() } : {}),
              // The buyers it is for, by company name — a word on the offer; each is addressed once it is prepared.
              audience: approvedBuyers.filter((b) => forBuyers.includes(b.id)).map((b) => b.companyName),
              languages: language === 'both' && own ? [own, 'en'] : [language],
              lines: parsed,
            },
            { onSuccess: () => { toast({ text: 'Drafted. Open it to see what still waits on the supplier.' }); onClose(); }, onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) },
          ),
      }}
      secondary={{ label: 'Cancel', onClick: onClose }}
    >
      <Field label="Title"><TextInput aria-label="Title" value={d.title} onChange={set('title')} /></Field>
      <Field label="Market" hint="Two letters, e.g. SE."><TextInput aria-label="Market" value={d.marketCode} onChange={set('marketCode')} maxLength={2} /></Field>
      <Field label="Supplier">
        <Select
          aria-label="Supplier"
          value={d.supplierId}
          onChange={set('supplierId')}
          options={[{ value: '', label: 'Choose one', disabled: true }, ...(suppliers.data?.suppliers ?? []).map((x) => ({ value: x.id, label: `${x.name} (${x.country})` }))]}
        />
      </Field>
      <Field label="Currency" hint="Three letters, e.g. SEK."><TextInput aria-label="Currency" value={d.currency} onChange={set('currency')} maxLength={3} /></Field>
      <Field label="Language" hint={own ? `${code}’s own language is ${own}; English beside it is the usual choice.` : 'English until a market is named.'}>
        <Select
          aria-label="Language"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          options={[{ value: 'en', label: 'English' }, ...(own && own !== 'en' ? [{ value: own, label: own }, { value: 'both', label: `${own} with English beside it` }] : [])]}
        />
      </Field>
      <fieldset className="m-0 flex flex-col gap-1 border-0 p-0">
        <legend className="mb-1 text-label uppercase tracking-label text-meta">For which buyers</legend>
        {approvedBuyers.length === 0 ? (
          <p className="m-0 text-dense text-meta">{code ? `No approved buyer in ${code} yet — the lead approves them on Buyers.` : 'Name the market first.'}</p>
        ) : (
          approvedBuyers.map((b) => (
            <label key={b.id} className="flex min-h-tap items-center gap-2 text-dense">
              <input type="checkbox" checked={forBuyers.includes(b.id)} onChange={(e) => setForBuyers((x) => (e.target.checked ? [...x, b.id] : x.filter((y) => y !== b.id)))} />
              {b.companyName} · {b.roleTitle}
            </label>
          ))
        )}
      </fieldset>
      <Field label="Valid until" hint="YYYY-MM-DD"><TextInput aria-label="Valid until" value={d.validUntil ?? ''} onChange={set('validUntil')} /></Field>
      <Field label="Incoterms"><TextInput aria-label="Incoterms" value={d.incoterms ?? ''} onChange={set('incoterms')} /></Field>
      <Field label="Lines" hint={`One a row: what it says | the value | the supplier's answer it rests on (one of ${SUPPLIER_FIELDS.map((f) => f.key).join(', ')}). ${parsed.length} line(s).`}>
        <Textarea aria-label="Lines" value={lines} onChange={(e) => setLines(e.target.value)} className="min-h-[120px]" placeholder={'Price per kg | 38.50 | price_per_kg\nLead time | 10 days | lead_time'} />
      </Field>
    </Dialog>
  );
}

/** `label | value | dependsOn` a row; a row with no label is skipped, and at most forty are taken (the route's own bound). */
export function offerLines(text: string): OfferDraft['lines'] {
  return text
    .split('\n')
    .map((row) => row.split('|').map((c) => c.trim()))
    .filter((cells) => cells[0] !== undefined && cells[0] !== '')
    .slice(0, 40)
    .map(([label, value, dependsOn]) => ({ label: label!, ...(value ? { value } : {}), ...(dependsOn ? { dependsOn } : {}) }));
}
